<script setup lang="ts">
import { usePwaInstallAction } from '~/shared-kernel/presentation/composables/usePwaInstallAction'
const { t } = useI18n()
const { installation, instructionsOpen, failed, install } = usePwaInstallAction()
const route = useRoute()
const staffLayout = computed(() => route.meta.layout === 'staff')
const mobileNavigationReady = useState<boolean>('customer-mobile-install-host', () => false)
const mobileViewport = ref(false)
const docked = computed(() => mobileNavigationReady.value && mobileViewport.value)
let mobileMedia: MediaQueryList | null = null
function syncMobileViewport() { mobileViewport.value = mobileMedia?.matches ?? false }
onMounted(() => {
  mobileMedia = window.matchMedia('(max-width: 42rem)')
  syncMobileViewport()
  mobileMedia.addEventListener('change', syncMobileViewport)
})
onBeforeUnmount(() => mobileMedia?.removeEventListener('change', syncMobileViewport))

watch(() => installation?.installed.value, installed => { if (installed) instructionsOpen.value = false })
</script>

<template>
  <Teleport :to="docked ? '#customer-mobile-install' : '#teleports'">
    <TqButton
      v-if="!staffLayout && installation?.visible.value"
      class="pwa-install-button"
      :class="{ 'pwa-install-button--docked': docked }"
      :variant="docked ? 'ghost' : 'primary'"
      icon="i-lucide-download"
      :title="t('app.install.action')"
      :icon-only-label="t('app.install.action')"
      :loading="installation.installing.value"
      :aria-haspopup="installation.ios || !installation.available.value ? 'dialog' : undefined"
      data-pwa-install
      @click="install"
    >{{ docked ? t('app.install.shortAction') : t('app.install.action') }}</TqButton>
  </Teleport>
  <UModal v-model:open="instructionsOpen" :title="t('app.install.action')" :description="t('app.install.onlineOnly')">
    <template #body>
      <p v-if="failed" role="alert">{{ t('app.install.failed') }}</p>
      <ol v-if="installation?.ios" class="list-decimal space-y-3 pl-5">
        <li>{{ t('app.install.iosShare') }}</li>
        <li>{{ t('app.install.iosAdd') }}</li>
      </ol>
      <ol v-else class="list-decimal space-y-3 pl-5">
        <li>{{ t('app.install.browserMenu') }}</li>
        <li>{{ t('app.install.browserInstall') }}</li>
      </ol>
      <p v-if="!installation?.ios" class="mt-4">{{ t('app.install.browserAvailability') }}</p>
    </template>
  </UModal>
</template>

<style scoped>
.pwa-install-button { position: fixed; z-index: 45; inset-inline-start: 1rem; bottom: calc(6rem + env(safe-area-inset-bottom)); max-width: calc(100% - 2rem); white-space: normal; box-shadow: var(--tq-shadow-soft); }
.pwa-install-button--docked { --tq-ghost-action: transparent; --tq-ghost-action-border: transparent; --tq-on-ghost-action: var(--tq-accent-readable); position: static; align-items: flex-start; width: 100%; max-width: none; min-width: 0; min-height: 3.25rem; padding: .15rem 0; font-size: .66rem; letter-spacing: -.02em; line-height: 1.15; box-shadow: none; }
.pwa-install-button--docked :deep(.tq-button__content) { flex-direction: column; gap: .2rem; }
.pwa-install-button--docked :deep(.tq-button__content > .iconify) { width: 1.25rem; height: 1.25rem; margin-block: .32rem; }
@media (min-width: 64rem) { .pwa-install-button { bottom: 1.5rem; } }
</style>
